import type { AuthIdentity, MainApiProfile, UserProfile } from '@/features/auth/types'

/**
 * Folds `GET /v1/profiles` into the app's user model.
 *
 * The two halves come from different services: the main API owns the personal
 * details, while roles and `mustChangePassword` only ever appear in the auth
 * service's login response and JWT claims. Neither is authoritative alone.
 */
export function toUserProfile(dto: MainApiProfile, identity: AuthIdentity): UserProfile {
  return {
    id: dto.id ?? identity.id,
    name: displayName(dto, identity),
    username: identity.username || dto.email?.split('@')[0] || '',
    email: dto.email ?? '',
    // Roles stay the auth service's. `dto.roleNames` is the main API's separate
    // vocabulary (`CUSTOMER` vs `administrator`) and answers a different
    // question, so it is not merged in here.
    roles: identity.roles,
    mustChangePassword: identity.mustChangePassword,
    phone: dto.phoneNumber ?? null,
    // The endpoint exposes no status or enabled flag at all — reaching it with a
    // live token is itself the evidence the account is usable.
    status: 'active',
    photoUrl: dto.photo?.url ?? null,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

/**
 * A person's name, never an email address.
 *
 * The auth service identifies accounts by email, so both the login response's
 * `username` and the JWT's claims routinely carry the full address — which is
 * how "gopizza@tenant.com" ends up where a name belongs. An address is reduced
 * to its local part; anything else passes through untouched.
 *
 * The address itself is not lost: it stays on `user.email`, which is what any
 * screen that genuinely needs to show it should read.
 */
export function toDisplayName(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? ''
  const at = trimmed.indexOf('@')

  return at > 0 ? trimmed.slice(0, at) : trimmed
}

/**
 * `fullName` is frequently null while the name parts are populated, and both can
 * be null on an account that has only ever been identified by email — so this
 * walks down to whatever is actually present rather than rendering "null".
 */
function displayName(dto: MainApiProfile, identity: AuthIdentity): string {
  const parts = [dto.firstName, dto.lastName].filter(Boolean).join(' ').trim()

  return (
    dto.fullName?.trim() ||
    parts ||
    toDisplayName(identity.username) ||
    toDisplayName(dto.email) ||
    'Account'
  )
}
